<template>
  <div class="p-0">
    <div class="flex justify-between items-center mb-6 gap-4">
      <h1 class="text-2xl font-bold text-gray-800">{{ pageTitle }}</h1>
      <div class="flex gap-2 items-center">
        <router-link
          v-if="showBatchesAndCreatedByMeNav"
          to="/task-batches"
          class="px-4 py-2 text-sm font-medium bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
        >
          Lotes de tareas
        </router-link>
        <router-link
          v-if="showBatchesAndCreatedByMeNav"
          to="/tasks/created-by-me"
          class="px-4 py-2 text-sm font-medium bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
        >
          Creadas por mí
        </router-link>
        <button
          @click="goToCreate"
          class="px-4 py-2 text-gray-200 bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          + Crear tarea
        </button>
      </div>
    </div>

    <!-- Tabs principales (solo admin / administrative / executive) -->
    <div v-if="showRoleTabs" class="flex flex-wrap gap-2 mb-6">
      <button
        type="button"
        @click="setMainTab('created_by_me')"
        :class="[
          'px-4 py-2 rounded-lg text-sm font-semibold border transition-colors',
          mainTab === 'created_by_me'
            ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
            : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
        ]"
      >
        Asignadas a personal
      </button>
      <button
        type="button"
        @click="setMainTab('mine')"
        :class="[
          'px-4 py-2 rounded-lg text-sm font-semibold border transition-colors',
          mainTab === 'mine'
            ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
            : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
        ]"
      >
        Mis tareas
      </button>
    </div>

    <!-- Filters -->
    <div class="flex flex-col md:flex-row gap-3 mb-4">
      <div class="flex-1">
        <label class="block text-xs font-medium text-gray-500 mb-1">Estado</label>
        <select
          v-model="statusTab"
          @change="fetchTasks"
          class="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-indigo-600"
        >
          <option value="todas">Todas</option>
          <option value="pendiente">Pendientes</option>
          <option value="revisar">Revisar</option>
          <option value="en_progreso">En progreso</option>
          <option value="bloqueada">Bloqueadas</option>
          <option value="vencida">Vencidas</option>
          <option value="completada">Completadas</option>
        </select>
      </div>

      <div class="w-full md:w-64">
        <label class="block text-xs font-medium text-gray-500 mb-1">Prioridad</label>
        <select
          v-model="priorityFilter"
          @change="fetchTasks"
          class="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-indigo-600"
        >
          <option value="">Todas</option>
          <option value="baja">Baja</option>
          <option value="media">Media</option>
          <option value="alta">Alta</option>
          <option value="urgente">Urgente</option>
        </select>
      </div>

      <template v-if="showResponsibleRoleFilters">
        <div class="w-full md:w-52">
          <label class="block text-xs font-medium text-gray-500 mb-1">Rol del responsable</label>
          <select
            v-model="filterRoleId"
            @change="onResponsibleRoleChange"
            class="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-indigo-600"
          >
            <option value="">Elegir rol…</option>
            <option v-for="r in allRoles" :key="r.id" :value="r.id">
              {{ roleOptionLabel(r.name) }}
            </option>
          </select>
        </div>
        <div class="w-full md:w-64">
          <label class="block text-xs font-medium text-gray-500 mb-1">Responsable</label>
          <select
            v-model="responsibleUserFilter"
            @change="fetchTasks"
            :disabled="!filterRoleId"
            class="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-indigo-600 disabled:bg-gray-50 disabled:text-gray-400"
          >
            <option value="">— Persona —</option>
            <option v-for="u in usersForResponsibleRole" :key="userRowKey(u)" :value="userRowId(u)">
              {{ userRowLabel(u) }}
            </option>
          </select>
        </div>
      </template>

      <div class="w-full md:w-64">
        <label class="block text-xs font-medium text-gray-500 mb-1">Buscar</label>
        <div class="flex items-center gap-2">
          <input
            v-model="search"
            @keyup.enter="fetchTasks"
            type="text"
            class="flex-1 border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-indigo-600"
            placeholder="Buscar..."
          />
          <button
            @click="fetchTasks"
            class="px-3 py-2 text-gray-100 bg-gray-800 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Buscar
          </button>
        </div>
      </div>
    </div>

    <!-- Tabs (se muestran como redundantes para calzar con los mocks) -->
    <div class="flex flex-wrap gap-2 mb-4">
      <button
        v-for="tab in tabs"
        :key="tab.value"
        @click="setStatusTab(tab.value)"
        :class="[
          'px-3 py-1.5 rounded-full text-sm font-medium border transition-colors',
          statusTab === tab.value
            ? 'bg-indigo-600 text-white border-indigo-600'
            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
        ]"
      >
        {{ tab.label }}
      </button>
    </div>

    <!-- Loading / Error / Empty -->
    <div v-if="loading" class="py-10">
      <Loading />
    </div>

    <div v-else-if="error" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
      {{ error }}
    </div>

    <div v-else-if="tasks.length === 0" class="bg-gray-50 border border-gray-200 rounded-md p-6 text-center text-gray-600">
      No hay tareas para los filtros seleccionados.
    </div>

    <!-- List -->
    <div v-else class="space-y-4">
      <div
        v-for="task in tasks"
        :key="task.id || task.task_id"
        class="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
      >
        <div class="flex items-start justify-between gap-3">
          <span
            class="px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap"
            :class="statusClass(task.status)"
          >
            {{ statusLabel(task.status) }}
          </span>
          <span
            class="px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap"
            :class="priorityClass(task.priority)"
          >
            {{ priorityLabel(task.priority) }}
          </span>
        </div>

        <h2 class="mt-2 text-lg font-semibold text-gray-900">
          <span
            @click="openTask(task.id || task.task_id)"
            class="text-blue-600 hover:text-blue-800 cursor-pointer underline"
          >
            {{ task.title || '-' }}
          </span>
        </h2>

        <div class="mt-2 text-sm text-gray-700">
          <div>
            <span class="font-medium text-gray-500">Categoría:</span>
            {{ categoryName(task) }}
          </div>
          <div>
            <span class="font-medium text-gray-500">Responsable:</span>
            {{ responsibleName(task) }}
          </div>
          <div v-if="task.due_at">
            <span class="font-medium text-gray-500">Vence:</span>
            {{ formatDate(task.due_at) }}
          </div>
        </div>

        <div class="mt-4 flex justify-end gap-3">
          <button
            @click="openTask(task.id || task.task_id)"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Detalle
          </button>
          <button
            v-if="canEditTask(task)"
            @click="editTask(task)"
            class="px-4 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 rounded-md hover:bg-indigo-100"
          >
            Editar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TasksView',
  components: {},
  data() {
    return {
      /** Solo admin / administrative / executive: 'created_by_me' (por defecto) | 'mine' */
      mainTab: 'created_by_me',
      tasks: [],
      loading: false,
      error: null,
      search: '',
      priorityFilter: '',
      statusTab: 'todas',
      tabs: [
        { value: 'todas', label: 'Todas' },
        { value: 'pendiente', label: 'Pendientes' },
        { value: 'revisar', label: 'Revisar' },
        { value: 'en_progreso', label: 'En progreso' },
        { value: 'bloqueada', label: 'Bloqueadas' },
        { value: 'vencida', label: 'Vencidas' },
        { value: 'completada', label: 'Completadas' },
      ],
      /** Para resolver responsable por ID cuando la API no envía nombre */
      teachers: [],

      /** Solo role === 'admin': filtro por lista de usuarios según rol */
      allRoles: [],
      filterRoleId: '',
      usersForResponsibleRole: [],
      responsibleUserFilter: '',
    };
  },
  computed: {
    isAdmin() {
      return this.$store.state.authentication?.user?.role === 'admin';
    },
    currentUserId() {
      return (
        this.$store.state.authentication?.user?.user_id ||
        this.$store.state.authentication?.user?.id ||
        ''
      );
    },
    showRoleTabs() {
      const role = this.$store.state.authentication?.user?.role;
      return ['admin', 'administrative', 'executive'].includes(role);
    },
    /** Enlaces Lotes / Creadas por mí: solo admin, executive, director */
    showBatchesAndCreatedByMeNav() {
      const role = this.$store.state.authentication?.user?.role;
      return ['admin', 'executive', 'director', 'administrative'].includes(role);
    },
    /** En "Mis tareas" la lista es solo del usuario logueado (mine); no filtrar por rol/responsable. */
    showResponsibleRoleFilters() {
      if (!this.showBatchesAndCreatedByMeNav) return false;
      if (this.showRoleTabs && this.mainTab === 'mine') return false;
      return true;
    },
    pageTitle() {
      if (!this.showRoleTabs) return 'Mis tareas';
      return this.mainTab === 'created_by_me' ? 'Tareas' : 'Mis tareas';
    },
  },
  created() {
    // Soporte para filtrar por batch desde otras vistas.
    if (this.$route.query.status) this.statusTab = this.$route.query.status;
    if (this.$route.query.priority) this.priorityFilter = this.$route.query.priority;
    if (this.$route.query.search) this.search = this.$route.query.search;
    if (this.showBatchesAndCreatedByMeNav && this.$route.query.filter_role_id) {
      this.filterRoleId = String(this.$route.query.filter_role_id);
    }
    if (
      this.showBatchesAndCreatedByMeNav &&
      this.$route.query.filter_role_id &&
      this.$route.query.responsible_user_id
    ) {
      this.responsibleUserFilter = String(this.$route.query.responsible_user_id);
    }
    const boot = async () => {
      if (this.showBatchesAndCreatedByMeNav) {
        await this.ensureRolesList();
        if (this.filterRoleId) await this.loadUsersForRole(this.filterRoleId);
      }
      await this.fetchTasks();
    };
    boot();
  },
  watch: {
    '$route.query.batch_id'() {
      this.fetchTasks();
    },
  },
  methods: {
    unwrapData(resp) {
      // API suele devolver { data: ... } o { data: { data: ... } }
      return resp?.data?.data ?? resp?.data?.data?.data ?? resp?.data ?? [];
    },
    goToCreate() {
      this.$router.push('/tasks/create');
    },
    setStatusTab(value) {
      this.statusTab = value;
      this.fetchTasks();
    },
    setMainTab(value) {
      this.mainTab = value;
      if (value === 'mine' && this.isAdmin) {
        this.filterRoleId = '';
        this.responsibleUserFilter = '';
        this.usersForResponsibleRole = [];
      }
      if (value === 'mine' && this.$route.query.batch_id) {
        const { batch_id: _b, ...rest } = this.$route.query;
        this.$router.replace({ path: this.$route.path, query: rest });
        return;
      }
      this.fetchTasks();
    },
    statusLabel(status) {
      const s = (status || '').toLowerCase();
      const map = {
        pendiente: 'Pendiente',
        en_progreso: 'En progreso',
        bloqueada: 'Bloqueada',
        revisar: 'Revisar',
        completada: 'Completada',
        cancelada: 'Cancelada',
        vencida: 'Vencida',
      };
      return map[s] || status || '-';
    },
    statusClass(status) {
      const s = (status || '').toLowerCase();
      const map = {
        pendiente: 'bg-yellow-100 text-yellow-900 border-yellow-200',
        revisar: 'bg-orange-100 text-orange-900 border-orange-200',
        en_progreso: 'bg-blue-100 text-blue-900 border-blue-200',
        bloqueada: 'bg-purple-100 text-purple-900 border-purple-200',
        completada: 'bg-green-100 text-green-900 border-green-200',
        vencida: 'bg-red-100 text-red-900 border-red-200',
        cancelada: 'bg-gray-100 text-gray-800 border-gray-200',
      };
      return map[s] || 'bg-gray-100 text-gray-800 border-gray-200';
    },
    priorityLabel(priority) {
      const p = (priority || '').toLowerCase();
      const map = {
        baja: 'Baja',
        media: 'Media',
        alta: 'Alta',
        urgente: 'Urgente',
      };
      return map[p] || priority || '-';
    },
    priorityClass(priority) {
      const p = (priority || '').toLowerCase();
      const map = {
        baja: 'bg-blue-50 text-blue-700 border-blue-100',
        media: 'bg-indigo-50 text-indigo-700 border-indigo-100',
        alta: 'bg-orange-50 text-orange-700 border-orange-100',
        urgente: 'bg-red-50 text-red-700 border-red-100',
      };
      return map[p] || 'bg-gray-100 text-gray-800 border-gray-200';
    },
    categoryName(task) {
      return (
        task?.category?.name ||
        task?.category_name ||
        task?.task_category?.name ||
        task?.categoryTitle ||
        '-'
      );
    },
    responsibleUserId(task) {
      const t = task || {};
      return (
        t.responsible_user_id ||
        t.responsibleUserId ||
        t.responsible_user?.id ||
        t.assignee_id ||
        t.assignee?.id ||
        t.user_id ||
        t.user?.id ||
        ''
      );
    },
    taskCreatorId(task) {
      const t = task || {};
      return (
        t.created_by_user_id ||
        t.attributes?.created_by_user_id ||
        t.createdByUserId ||
        ''
      );
    },
    canEditTask(task) {
      const creatorId = this.taskCreatorId(task);
      const currentUserId = this.currentUserId;
      if (creatorId == null || creatorId === '' || currentUserId == null || currentUserId === '') {
        return false;
      }
      return String(creatorId) === String(currentUserId);
    },
    teacherLabel(userId) {
      if (userId == null || userId === '') return '—';
      const t = this.teachers.find((x) => String(x.id) === String(userId));
      if (!t) return String(userId);
      const lname = t.Profile?.lastname || '';
      const fname = t.Profile?.firstname || '';
      if (lname || fname) return `${lname}, ${fname}`.trim();
      return t.name || t.email || String(userId);
    },
    responsibleName(task) {
      const t = task || {};
      const ru = t.responsible_user;
      const embedded =
        (typeof ru === 'object' && ru
          ? ru.name ||
            ru.email ||
            (ru.Profile && `${ru.Profile.lastname || ''}, ${ru.Profile.firstname || ''}`.trim())
          : '') ||
        t.responsible?.name ||
        t.responsible_user_name ||
        t.responsibleUserName ||
        t.assignee?.name ||
        t.assignee_name ||
        t.user?.name ||
        t.user_name ||
        '';
      if (embedded) return embedded;
      const uid = this.responsibleUserId(t);
      return uid ? this.teacherLabel(uid) : '—';
    },
    async ensureTeachers() {
      if (this.teachers.length) return;
      try {
        const res = await this.$http.get('/teachers');
        const payload = this.unwrapData(res);
        const list = Array.isArray(payload) ? payload : payload?.data || [];
        this.teachers = Array.isArray(list) ? list : [];
      } catch {
        /* responsibleName puede seguir mostrando datos embebidos o el ID */
      }
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      const d = new Date(dateString);
      if (Number.isNaN(d.getTime())) return String(dateString);
      return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
    roleOptionLabel(name) {
      const n = (name || '').toLowerCase();
      const map = {
        admin: 'Administrador',
        administrative: 'Administrativo',
        executive: 'Ejecutivo',
        teacher: 'Docente',
        rti: 'Rti',
        director: 'Director',
        janitor: 'Personal de limpieza',
        student_assistant: 'Preceptor/a',
      };
      return map[n] || name || '—';
    },
    userRowId(entry) {
      if (!entry) return '';
      const u = entry.User?.id ?? entry.user_id ?? entry.id;
      return u != null && u !== '' ? String(u) : '';
    },
    userRowKey(entry) {
      const id = this.userRowId(entry);
      return id || String(entry?.id ?? '');
    },
    userRowLabel(entry) {
      if (!entry) return '';
      if (entry.Profile && (entry.Profile.lastname || entry.Profile.firstname)) {
        return `${entry.Profile.lastname || ''}, ${entry.Profile.firstname || ''}`.trim();
      }
      return entry.name || entry.email || this.userRowId(entry) || '—';
    },
    async ensureRolesList() {
      if (!this.showBatchesAndCreatedByMeNav || this.allRoles.length) return;
      try {
        const res = await this.$http.get('/roles');
        const raw = res.data?.data ?? res.data;
        let list = Array.isArray(raw) ? raw : [];
        
        const filtered = ['admin', 'teacher', 'student']
        

        list =  list.filter(el => !filtered.includes(el.name))
                
        this.allRoles = [...list].sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', 'es')
        );
      } catch {
        this.allRoles = [];
      }
    },
    async loadUsersForRole(roleId) {
      if (!roleId) {
        this.usersForResponsibleRole = [];
        return;
      }
      try {
        const res = await this.$http.get(`/user-by-role-id/${roleId}`);
        const raw = res.data?.data ?? res.data ?? [];
        this.usersForResponsibleRole = Array.isArray(raw) ? raw : [];
      } catch {
        this.usersForResponsibleRole = [];
      }
    },
    async onResponsibleRoleChange() {
      this.responsibleUserFilter = '';
      this.usersForResponsibleRole = [];
      if (!this.filterRoleId) {
        await this.fetchTasks();
        return;
      }
      await this.loadUsersForRole(this.filterRoleId);
      await this.fetchTasks();
    },
    buildParams() {
      const params = {};

      if (this.showRoleTabs && this.mainTab === 'created_by_me') {
        params.created_by_me = true;
        if (this.$route.query.batch_id) {
          params.batch_id = this.$route.query.batch_id;
        }
      } else {
        // "Mis tareas" (pestaña o vista sin lotes): siempre mine; no reutilizar batch_id del query.
        params.mine = true;
      }

      if (this.statusTab && this.statusTab !== 'todas') params.status = this.statusTab;
      if (this.priorityFilter) params.priority = this.priorityFilter;
      if (this.search) params.search = this.search;
      if (this.showResponsibleRoleFilters && this.responsibleUserFilter) {
        params.responsible_user_id = this.responsibleUserFilter;
      }

      return params;
    },
    async fetchTasks() {
      this.loading = true;
      this.error = null;
      try {
        if (this.showBatchesAndCreatedByMeNav) await this.ensureRolesList();
        await this.ensureTeachers();
        const res = await this.$http.get('/tasks', { params: this.buildParams() });
        const payload = this.unwrapData(res);
        const list = Array.isArray(payload) ? payload : payload?.data || payload?.tasks || [];
        this.tasks = Array.isArray(list) ? list : [];
      } catch (err) {
        this.error =
          err?.response?.data?.message ||
          err?.response?.data?.data?.message ||
          err?.message ||
          'Error al cargar tareas';
      } finally {
        this.loading = false;
      }
    },
    openTask(id) {
      if (!id) return;
      this.$router.push(`/tasks/${id}`);
    },
    editTask(task) {
      const id = task?.id || task?.task_id;
      if (!id) return;
      this.$router.push({ path: `/tasks/${id}`, query: { edit: '1' } });
    },
  },
};
</script>

<style scoped>
</style>

